// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import DataTable from "@insite/mobius/DataTable";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Filter from "@insite/mobius/Icons/Filter";
import {
    Clickable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
} from "@insite/mobius/index";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@pacific/App/Components/Notify/Notify";
import loadBackorders from "@pacific/App/Handlers/Backorders/LoadBackorders";
import myAccountFilterSectionStyles from "@pacific/App/SharedStyle/MyAccount/List/MyAccountFilterSectionStyles";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import BackorderSummaryCard, {
    BackorderSummaryCardStyles,
} from "@pacific/App/Widgets/RecentBackorders/BackorderSummaryCard";
import { color, xScreen } from "@pacific/XDL/Utilities";
import debounce from "lodash/debounce";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

export interface BackordersStyles {
    filterToggle?: ClickablePresentationProps;
    filter?: InjectableCss;
    toggleFilterWrapper?: InjectableCss;
    countWrapper?: InjectableCss;
    searchProductWrapper?: InjectableCss;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    toggleFilterIcon?: IconPresentationProps;
    clearFilterButtonWrapper?: InjectableCss;
    clearFiltersButton?: ButtonProps;
    countButtonWrapper?: InjectableCss;
    orderNumberWrapper?: InjectableCss;
    poWrapper?: InjectableCss;
    dateRangeWrapper?: InjectableCss;
    fromDate?: DatePickerPresentationProps;
    toDate?: DatePickerPresentationProps;
    tableWrapper?: InjectableCss;
}

export const backordersStyles: BackordersStyles = {
    filterToggle: {
        ...myAccountFilterSectionStyles.toggleFilterClickable,
    },
    filter: {
        ...myAccountFilterSectionStyles.searchFormStyles.wrapper,
        css: css`
            ${myAccountFilterSectionStyles.searchFormStyles.wrapper?.css}
            display: flex;
            flex-wrap: wrap;
            gap: 15px;
        `,
    },
    toggleFilterWrapper: {
        css: css`
            ${myAccountFilterSectionStyles.toggleFilterGridItem?.css}
        `,
    },
    countWrapper: {
        css: css`
            ${myAccountFilterSectionStyles.countGridItem?.css}
            ${xScreen("lte", "xs")} {
                margin-bottom: 8px;
            }
        `,
    },
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    productImage: {
        width: "50px",
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            margin-top: 5px;
        `,
    },
    clearFilterButtonWrapper: {
        css: css`
            width: 100%;
            margin-top: 10px;
            display: flex;
            justify-content: flex-end;
        `,
    },
    toggleFilterIcon: {
        src: Filter,
        size: 24,
        css: css`
            display: none;
        `,
    },
    clearFiltersButton: {
        variant: "tertiary",
        css: css`
            margin-bottom: 5px;
        `,
    },
    countButtonWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                align-items: flex-start;
            }
        `,
    },
    searchProductWrapper: {
        css: css`
            width: calc((100% - 15px) / 2);
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    orderNumberWrapper: {
        css: css`
            width: calc((100% - 15px) / 2);
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    poWrapper: {
        css: css`
            width: calc((100% - 15px) / 2);
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    dateRangeWrapper: {
        css: css`
            width: calc((100% - 15px) / 2);
            display: flex;
            gap: 15px;
            & > div {
                flex-grow: 1;
            }
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    toDate: {
        cssOverrides: {
            inputSelect: css`
                width: 100%;
            `,
            formField: css`
                width: 100%;
            `,
        },
        labelProps: {
            color: "common.background",
        },
    },
    fromDate: {
        cssOverrides: {
            inputSelect: css`
                width: 100%;
            `,
            formField: css`
                width: 100%;
            `,
        },
    },
    tableWrapper: {
        css: css`
            overflow: auto;
        `,
    },
};

const styles = backordersStyles;

const mapStateToProps = (state: CustomAppState) => ({
    session: getSession(state),
    backorders: state.backorders.backOrders,
});

const mapDispatchToProps = {
    getAutocompleteModel,
    loadBackorders,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const Backorders: FC<Props> = ({ loadBackorders, getAutocompleteModel, session, backorders }) => {
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [filterByPo, setFilterByPo] = useState<string>("");
    const [filterByOrderNumber, setFilterByOrderNumber] = useState<string>("");
    const [filterByFromDate, setFilterByFromDate] = useState<Date>();
    const [filterByToDate, setFilterByToDate] = useState<Date>();
    const [filterByProductErpNumber, setFilterByProductErpNumber] = useState<string>("");
    const [options, setOptions] = useState<OptionObject[]>([]);

    useEffect(() => {
        loadBackorders({});
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            loadBackorders({
                productErpNumber: filterByProductErpNumber,
                orderNumber: filterByOrderNumber,
                customerPo: filterByPo,
                fromDate: convertDateToDateOnlyString(filterByFromDate),
                toDate: convertDateToDateOnlyString(filterByToDate),
            });
        }, 1000);
        return () => clearTimeout(timer);
    }, [filterByOrderNumber, filterByPo]);

    const poChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setFilterByPo(event.target.value);
    };

    const orderNumberChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setFilterByOrderNumber(event.target.value);
    };

    const toggleFilter = () => {
        setShowFilters(!showFilters);
    };

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (selectedDay !== filterByFromDate) {
            setFilterByFromDate(selectedDay);
            loadBackorders({
                productErpNumber: filterByProductErpNumber,
                orderNumber: filterByOrderNumber,
                customerPo: filterByPo,
                fromDate: convertDateToDateOnlyString(selectedDay),
                toDate: convertDateToDateOnlyString(filterByToDate),
            });
        }
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (selectedDay !== filterByToDate) {
            setFilterByToDate(selectedDay);
            loadBackorders({
                productErpNumber: filterByProductErpNumber,
                orderNumber: filterByOrderNumber,
                customerPo: filterByPo,
                fromDate: convertDateToDateOnlyString(filterByFromDate),
                toDate: convertDateToDateOnlyString(selectedDay),
            });
        }
    };

    const onSelectionChangeHandler = (value?: string) => {
        setFilterByProductErpNumber(value || "");
        loadBackorders({
            productErpNumber: value,
            orderNumber: filterByOrderNumber,
            customerPo: filterByPo,
            fromDate: convertDateToDateOnlyString(filterByFromDate),
            toDate: convertDateToDateOnlyString(filterByToDate),
        });
    };

    const clearFiltersClickHandler = () => {
        setFilterByProductErpNumber("");
        setFilterByOrderNumber("");
        setFilterByPo("");
        setFilterByFromDate(undefined);
        setFilterByToDate(undefined);
        loadBackorders({});
    };

    const debouncedSearchProducts = debounce((query: string) => {
        getAutocompleteModel({
            query,
            onSuccess: result => {
                if (!result?.products) {
                    return;
                }

                const newOptions = result.products.map(product => ({
                    optionText: product.erpNumber,
                    optionValue: product.erpNumber || undefined,
                    rowChildren: (
                        <StyledWrapper {...styles.optionWrapper} key={product.erpNumber}>
                            <StyledWrapper {...styles.imageWrapper}>
                                <LazyImage {...styles.productImage} src={product.image} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.autocompleteTitleText}>{product.title}</Typography>
                                <Typography {...styles.autocompleteErpText}>{product.erpNumber}</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    ),
                }));
                setOptions(newOptions);
            },
            onComplete(resultProps) {
                if (resultProps?.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps?.apiResult);
                }
            },
        });
    }, 300);

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        debouncedSearchProducts(event.target.value);
    };

    return (
        <StyledWrapper>
            <StyledWrapper>
                <StyledWrapper {...styles.countButtonWrapper}>
                    <StyledWrapper {...styles.countWrapper}>
                        {backorders.length} {translate("Orders")}
                    </StyledWrapper>
                    <StyledWrapper {...styles.toggleFilterWrapper}>
                        <Clickable
                            {...styles.filterToggle}
                            onClick={toggleFilter}
                            data-test-selector="backorders_toggleFilter"
                        >
                            <Icon {...styles.toggleFilterIcon} />
                        </Clickable>
                    </StyledWrapper>
                </StyledWrapper>
                {showFilters && (
                    <StyledWrapper {...styles.filter} {...{ showFilters }}>
                        <StyledWrapper {...styles.searchProductWrapper}>
                            {/* xng-change: update label */}
                            <DynamicDropdown
                                label={translate("Product #")}
                                onSelectionChange={onSelectionChangeHandler}
                                onInputChange={onInputChanged}
                                filterOption={() => true}
                                selected={filterByProductErpNumber}
                                options={options}
                                placeholder={translate("Enter keyword or item #")}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.orderNumberWrapper}>
                            <TextField
                                value={filterByOrderNumber}
                                label={translate("Order #")}
                                type="text"
                                onChange={orderNumberChangeHandler}
                                placeholder={translate("Enter Order #")}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.poWrapper}>
                            <TextField
                                value={filterByPo}
                                label={translate("PO #")}
                                type="text"
                                placeholder={translate("Enter PO #")}
                                onChange={poChangeHandler}
                            />
                        </StyledWrapper>

                        <StyledWrapper {...styles.dateRangeWrapper} data-test-selector="backorders_filterDateRange">
                            <DatePicker
                                {...styles.fromDate}
                                data-test-selector="backorders_filterFromDate"
                                label={translate("Date Range")}
                                selectedDay={filterByFromDate}
                                onDayChange={fromDateChangeHandler}
                                dateTimePickerProps={{
                                    clearIcon: null,
                                }}
                            />
                            <DatePicker
                                {...styles.toDate}
                                data-test-selector="backorders_filterToDate"
                                label={translate("To")}
                                selectedDay={filterByToDate}
                                onDayChange={toDateChangeHandler}
                                dateTimePickerProps={{
                                    clearIcon: null,
                                    minDate: filterByFromDate,
                                }}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.clearFilterButtonWrapper}>
                            <Button
                                {...styles.clearFiltersButton}
                                onClick={clearFiltersClickHandler}
                                data-test-selector="backorders_clearFiltersButton"
                            >
                                {translate("Clear Filters")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            {(!backorders || backorders.length === 0) && (
                <StyledWrapper>
                    <Notify type="warning">{translate("No backorders found")}</Notify>
                </StyledWrapper>
            )}
            {backorders && backorders.length > 0 && (
                <StyledWrapper {...styles.tableWrapper}>
                    <DataTable>
                        <DataTableHead>
                            <DataTableHeader>{translate("Product")}</DataTableHeader>
                            <DataTableHeader>{translate("Description")}</DataTableHeader>
                            <DataTableHeader>{translate("Order #")}</DataTableHeader>
                            <DataTableHeader>{translate("PO #")}</DataTableHeader>
                            <DataTableHeader>{translate("BO Qty")}</DataTableHeader>
                            <DataTableHeader>{translate("Order Date")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody>
                            {backorders.map(
                                (backorder, index) =>
                                    backorder !== null && (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <DataTableRow key={index}>
                                            <BackorderSummaryCard backorder={backorder} session={session} />
                                        </DataTableRow>
                                    ),
                            )}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Backorders),
    definition: {
        group: "Xngage Custom" as any,
    },
};

export default widgetModule;
